import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";
import { client } from "@/lib/api";
import { RivetsApiError } from "@rivets/core";
import { BrandLogo } from "@/components/BrandLogo";
import { Skeleton } from "@/components/Skeleton";
import { setNextPath } from "@/lib/nextPath";

export function InvitePage() {
  const navigate = useNavigate();
  const { token } = useParams({ strict: false }) as { token?: string };
  const { data: session, isPending } = authClient.useSession();
  const [accepted, setAccepted] = useState(false);

  const acceptMutation = useMutation({
    mutationFn: () => client.acceptInvite({ token: token ?? "" }),
    onSuccess: (res) => {
      setAccepted(true);
      setTimeout(() => navigate({ to: `/o/${res.org.id}` }), 600);
    },
  });

  // Preserve the invite so we can accept it right after the user authenticates.
  const returnTo = `/invite/${token ?? ""}`;
  function goAuth(dest: "/login" | "/register") {
    setNextPath(returnTo);
    navigate({ to: dest });
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Organization invite</h1>

        {isPending ? (
          <div className="panel">
            <div className="invite-summary"><Skeleton width="55%" height={16} /><Skeleton width="80%" height={14} /><Skeleton width="65%" height={14} /></div>
          </div>
        ) : !session?.user ? (
          <div className="panel">
            <p className="auth-subtitle" style={{ marginBottom: 12 }}>
              Sign in or create an account to accept this invite.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-block" onClick={() => goAuth("/login")}>
                Sign in
              </button>
              <button className="btn btn-primary btn-block" onClick={() => goAuth("/register")}>
                Create account
              </button>
            </div>
          </div>
        ) : (
          <div className="panel">
            <div className="invite-summary"><span className="overline">Invitation</span><strong>Join an organization in Rivets</strong><span>{session?.user ? `Signed in as ${session.user.email}` : "Sign in or create an account to continue."}</span></div>
            {accepted ? (
              <div className="auth-success-state" role="status"><div className="auth-state-icon">✓</div><strong>Invite accepted</strong><span>Opening your organization…</span></div>
            ) : (
              <>
                <p className="auth-subtitle" style={{ marginBottom: 12 }}>
                  Signed in as {session.user.email}. Accept this invite to join the organization.
                </p>
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => acceptMutation.mutate()}
                  disabled={acceptMutation.isPending || !token}
                >
                  {acceptMutation.isPending ? "Accepting…" : "Accept invite"}
                </button>
                {acceptMutation.isError && (
                  <div className="error-text">
                    {acceptMutation.error instanceof RivetsApiError
                      ? acceptMutation.error.message
                      : "Could not accept invite."}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
